import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import api from '../utils/api';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token') || localStorage.getItem('adminToken'),
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
  adminData: null,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        isAdmin: false,
      };
    case 'ADMIN_LOGIN_SUCCESS':
      return {
        ...state,
        adminData: action.payload.admin,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        isAdmin: true,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isAdmin: false,
        adminData: null,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isAdmin: false,
        adminData: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  // Set auth token in axios headers
  useEffect(() => {
    if (state.token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [state.token]);

  // Check if user or admin is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const userToken = localStorage.getItem('token');
      const adminToken = localStorage.getItem('adminToken');
      
      console.log('Auth check - userToken:', !!userToken, 'adminToken:', !!adminToken);
      
      if (adminToken) {
        // If adminToken exists, verify it's a valid admin
        try {
          const adminResponse = await api.get('/api/admin/me');
          if (adminResponse.data.success) {
            console.log('Auth check - Admin authenticated');
            dispatch({
              type: 'ADMIN_LOGIN_SUCCESS',
              payload: {
                admin: adminResponse.data.data,
                token: adminToken,
              },
            });
            return; // Exit early
          }
        } catch (error) {
          console.error('Admin auth check failed:', error);
          localStorage.removeItem('adminToken');
        }
      }
      
      if (userToken) {
        // If userToken exists, verify it's a valid user
        try {
          const userResponse = await api.get('/api/auth/profile');
          if (userResponse.data.success) {
            console.log('Auth check - User authenticated');
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: userResponse.data.data,
                token: userToken,
              },
            });
            return; // Exit early
          }
        } catch (error) {
          console.error('User auth check failed:', error);
          localStorage.removeItem('token');
        }
      }
      
      // If no valid tokens or authentication failed
      console.log('Auth check - No valid authentication found');
      dispatch({ type: 'SET_LOADING', payload: false });
    };

    checkAuth();
  }, []);

  const login = async (email, password, redirectTo = null) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await api.post('/api/auth/login', {
        email,
        password,
      });

      if (response.data.success) {
        const { user, token } = response.data.data;
        localStorage.setItem('token', token);
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token },
        });
        
        toast.success('Login successful!');
        if (redirectTo) {
          navigate(redirectTo);
        }
        return { success: true };
      } else {
        throw new Error(response.data.error || 'Login failed');
      }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Login failed';
      toast.error(message);
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, error: message };
    }
  };

  const adminLogin = async (email, password) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await api.post('/api/admin/login', {
        email,
        password,
      });

      if (response.data.success) {
        const { admin, token } = response.data.data;
        localStorage.setItem('adminToken', token);
        
        dispatch({
          type: 'ADMIN_LOGIN_SUCCESS',
          payload: { admin, token },
        });
        
        toast.success('Admin login successful!');
        navigate('/admin/dashboard', { replace: true });
        return { success: true };
      } else {
        throw new Error(response.data.error || 'Login failed');
      }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Login failed';
      toast.error(message);
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, error: message };
    }
  };

  const register = async (userData, redirectTo = null) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await api.post('/api/auth/register', userData);

      if (response.data.success) {
        const { user, token } = response.data.data;
        localStorage.setItem('token', token);
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token },
        });
        
        toast.success('Registration successful!');
        if (redirectTo) {
          navigate(redirectTo);
        }
        return { success: true };
      } else {
        throw new Error(response.data.error || 'Registration failed');
      }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Registration failed';
      toast.error(message);
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, error: message };
    }
  };

  const logout = () => {
    const wasAdmin = state.isAdmin;
    localStorage.removeItem('token');
    localStorage.removeItem('adminToken');
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
    navigate('/');
  };

  const logoutAdmin = () => {
    localStorage.removeItem('adminToken');
    dispatch({ type: 'LOGOUT' });
    toast.success('Admin signed out successfully');
    navigate('/admin/login');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/api/auth/profile', profileData);
      
      if (response.data.success) {
        dispatch({
          type: 'UPDATE_USER',
          payload: response.data.data,
        });
        toast.success('Profile updated successfully!');
        return { success: true };
      } else {
        throw new Error(response.data.error || 'Profile update failed');
      }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updatePreferences = async (preferences) => {
    try {
      const response = await api.put('/api/auth/preferences', preferences);
      if (response.data.success) {
        // Update user in state
        if (response.data.data) {
          dispatch({
            type: 'UPDATE_USER',
            payload: { ...state.user, preferences: response.data.data },
          });
        }
        toast.success('Preferences updated!');
        return { success: true, data: response.data.data };
      }
      throw new Error(response.data.error || 'Failed to update preferences');
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to update preferences';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const getBookmarks = async () => {
    try {
      const response = await api.get('/api/bookmarks/jobs');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to get bookmarks';
      return { success: false, error: message };
    }
  };

  const toggleBookmark = async (jobId) => {
    try {
      // First check if bookmark exists
      const checkResponse = await api.get(`/api/bookmarks/check/${jobId}`);
      let response;
      
      if (checkResponse.data.data.isBookmarked) {
        // Remove bookmark
        response = await api.delete(`/api/bookmarks/${jobId}`);
        // Update state to remove bookmark
        if (state.user) {
          const currentBookmarks = state.user.bookmarks || [];
          const updatedBookmarks = currentBookmarks.filter(
            b => b && b.jobId && (b.jobId._id || b.jobId) !== jobId
          );
          dispatch({
            type: 'UPDATE_USER',
            payload: { ...state.user, bookmarks: updatedBookmarks },
          });
        }
      } else {
        // Add bookmark
        response = await api.post(`/api/bookmarks/${jobId}`);
        // Update state to add bookmark
        if (state.user) {
          const currentBookmarks = state.user.bookmarks || [];
          const newBookmark = { jobId: jobId, addedAt: new Date() };
          dispatch({
            type: 'UPDATE_USER',
            payload: { ...state.user, bookmarks: [...currentBookmarks, newBookmark] },
          });
        }
      }

      if (response.data.success) {
        toast.success(checkResponse.data.data.isBookmarked ? 'Job removed from bookmarks' : 'Job added to bookmarks');
        return { success: true, isBookmarked: !checkResponse.data.data.isBookmarked };
      }

      throw new Error(response.data.error || 'Failed to update bookmark');
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to update bookmark';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const checkBookmark = async (jobId) => {
    try {
      const response = await api.get(`/api/bookmarks/check/${jobId}`);
      return { success: true, isBookmarked: response.data.data.isBookmarked };
    } catch (error) {
      return { success: false, isBookmarked: false };
    }
  };

  const updateUser = (userData) => {
    dispatch({
      type: 'UPDATE_USER',
      payload: userData,
    });
  };

  const value = {
    ...state,
    login,
    adminLogin,
    register,
    logout,
    logoutAdmin,
    updateProfile,
    updatePreferences,
    getBookmarks,
    toggleBookmark,
    checkBookmark,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
